/**
 * Pure utility functions extracted from App.jsx
 * No component dependencies — safe to import anywhere.
 */

// Generate unique IDs
export const generateId = () => Math.random().toString(36).substring(2, 9);

// Generate unique procedure ID
export const generateProcedureId = () => {
  return 'proc_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
};

// Generate version ID
export const generateVersionId = () => {
  return 'v_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
};

// Max versions to keep per procedure
export const MAX_VERSIONS = 50;

// CPF Formatting
export const formatCPF = (value) => {
  const digits = value.replace(/\D/g, '');
  const limited = digits.slice(0, 11);

  if (limited.length <= 3) {
    return limited;
  } else if (limited.length <= 6) {
    return `${limited.slice(0, 3)}.${limited.slice(3)}`;
  } else if (limited.length <= 9) {
    return `${limited.slice(0, 3)}.${limited.slice(3, 6)}.${limited.slice(6)}`;
  } else {
    return `${limited.slice(0, 3)}.${limited.slice(3, 6)}.${limited.slice(6, 9)}-${limited.slice(9)}`;
  }
};

// CPF Validation
export const validateCPF = (cpf) => {
  const digits = cpf.replace(/\D/g, '');

  if (digits.length !== 11) {
    return { valid: false, message: 'CPF deve ter 11 dígitos' };
  }

  if (/^(\d)\1{10}$/.test(digits)) {
    return { valid: false, message: 'CPF inválido' };
  }

  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(digits[i]) * (10 - i);
  }
  let remainder = sum % 11;
  let firstCheckDigit = remainder < 2 ? 0 : 11 - remainder;

  if (parseInt(digits[9]) !== firstCheckDigit) {
    return { valid: false, message: 'CPF inválido' };
  }

  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(digits[i]) * (11 - i);
  }
  remainder = sum % 11;
  let secondCheckDigit = remainder < 2 ? 0 : 11 - remainder;

  if (parseInt(digits[10]) !== secondCheckDigit) {
    return { valid: false, message: 'CPF inválido' };
  }

  return { valid: true, message: 'CPF válido' };
};

// Create a version snapshot
export const createVersionSnapshot = (procedure, note = '') => {
  const { versions: _versions, ...procedureData } = procedure;
  return {
    id: generateVersionId(),
    timestamp: new Date().toISOString(),
    note: note,
    data: JSON.parse(JSON.stringify(procedureData))
  };
};

// Calculate diff summary between two procedure states
export const calculateDiffSummary = (oldData, newData) => {
  const changes = [];

  const fields = [
    { key: 'details', label: 'detalhes' },
    { key: 'risks', label: 'riscos' },
    { key: 'preOp', label: 'pré-op' },
    { key: 'postOp', label: 'pós-op' }
  ];

  fields.forEach(({ key, label }) => {
    const oldLen = oldData?.[key]?.length || 0;
    const newLen = newData?.[key]?.length || 0;
    const diff = newLen - oldLen;
    if (diff > 0) changes.push(`+${diff} ${label}`);
    if (diff < 0) changes.push(`${diff} ${label}`);
  });

  if (oldData?.name !== newData?.name) changes.push('nome alterado');
  if (oldData?.description !== newData?.description) changes.push('descrição alterada');
  if (oldData?.recovery !== newData?.recovery) changes.push('recuperação alterada');

  return changes.length > 0 ? changes.join(', ') : 'sem alterações visíveis';
};

// Format timestamp for display
export const formatTimestamp = (isoString) => {
  const date = new Date(isoString);
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Format relative time
export const formatRelativeTime = (isoString) => {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'agora';
  if (diffMins < 60) return `há ${diffMins} min`;
  if (diffHours < 24) return `há ${diffHours}h`;
  if (diffDays < 7) return `há ${diffDays} dias`;
  return formatTimestamp(isoString);
};

// Calculate detailed diff between two arrays
export const calculateArrayDiff = (oldArr = [], newArr = []) => {
  const oldSet = new Set(oldArr);
  const newSet = new Set(newArr);

  const added = newArr.filter(item => !oldSet.has(item));
  const removed = oldArr.filter(item => !newSet.has(item));
  const unchanged = newArr.filter(item => oldSet.has(item));

  return { added, removed, unchanged };
};

// Check if text field changed
export const textChanged = (oldText, newText) => {
  return (oldText || '') !== (newText || '');
};
